import { supabase } from "@/lib/supabase";

export const SITE_URL = "https://oliviaflemming.co.uk";

export const JOURNAL_CATEGORIES = [
  "Weddings",
  "Hotels & Events",
  "Residential Entertainment",
  "Special Occasions",
  "Behind the Music",
] as const;

export type JournalPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  category: string;
  cover_image_url: string | null;
  cover_alt: string | null;
  seo_title: string | null;
  seo_description: string | null;
  author: string;
  status: "draft" | "published";
  featured: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
};

const table = () => supabase.from("journal_posts" as never) as any;

/** Published posts only, newest first (public pages). */
export async function listPublishedPosts(): Promise<JournalPost[]> {
  const { data, error } = await table()
    .select("*")
    .eq("status", "published")
    .order("published_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getPublishedPost(slug: string): Promise<JournalPost | null> {
  const { data, error } = await table().select("*").eq("slug", slug).eq("status", "published").maybeSingle();
  if (error) throw error;
  return data;
}

/** All posts including drafts (admin only, enforced by RLS). */
export async function listAllPosts(): Promise<JournalPost[]> {
  const { data, error } = await table().select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getPostById(id: string): Promise<JournalPost | null> {
  const { data, error } = await table().select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}

export async function savePost(post: Partial<JournalPost> & { id?: string }): Promise<JournalPost> {
  const { id, created_at, updated_at, ...rest } = post as any;
  const q = id ? table().update(rest).eq("id", id) : table().insert(rest);
  const { data, error } = await q.select("*").single();
  if (error) throw error;
  return data;
}

export async function deletePost(id: string) {
  const { error } = await table().delete().eq("id", id);
  if (error) throw error;
}

/** Uploads a photo and returns a long lived link (bucket is private, so a 10 year signed link). */
export async function uploadJournalImage(file: File): Promise<string> {
  const ext = file.name.split(".").pop() || "jpg";
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from("journal").upload(path, file, { contentType: file.type });
  if (error) throw error;
  const { data, error: e2 } = await supabase.storage.from("journal").createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
  if (e2 || !data) throw e2 ?? new Error("Could not create image link");
  return data.signedUrl;
}

export const slugify = (s: string) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

export const absoluteUrl = (u: string | null) => (!u ? undefined : u.startsWith("http") ? u : SITE_URL + u);
